import LeaseGenerationForm from 'src/models/leasing/LeaseGenerationForm';
import { Reducer, Action } from 'redux';
import { IRootState } from './index';
import { call, select, all, delay, put, takeEvery, fork, takeLatest } from 'redux-saga/effects';

import { actionCreators as layoutActionCreators } from 'src/reducers/Layout';
import { selectors as authSelectors } from 'src/reducers/Auth';
import { selectors as projectSelectors } from 'src/reducers/Project';

import Api from '../api';
import { replace } from 'connected-react-router';
import LeaseGenerationOwnerData from 'src/models/leasing/LeaseGenerationOwnerData';
// import { ODataOptions } from 'src/util/ODataBuilder';
import PagedResultSet from 'src/models/common/PagedResultSet';
import { ITableConfig } from 'src/components/common/table';
import IOwner from 'src/models/IOwner';
import Project from 'src/models/Project';
import Lease from 'src/models/leasehold/Lease';
import ContractGenerationListDto from 'src/models/leasing/ContractGenerationListDto';
import { ODataOptions, ODataOrderByDirection } from 'src/util/ODataBuilder';
import _ from 'lodash';

// Store
export interface ILeaseGenerationState {
    formData: LeaseGenerationForm;
    ownerData: LeaseGenerationOwnerData;
    leaseCollection: Array<Lease>;
    // agreements: PagedResultSet<LeaseGenerationForm>;
    agreements: Array<Lease>;
    generatedContracts: PagedResultSet<ContractGenerationListDto>;
    agreementsPageTableConfig: ITableConfig;
    isFormDataComplete: boolean;
    isOwnerDataComplete: boolean;
    defaultLessee: IOwner;
    dataLoaded: boolean;
}

const LeaseGenerationInitialState: ILeaseGenerationState = {
    formData: undefined,
    ownerData: undefined,
    leaseCollection: undefined,
    agreements: undefined,
    generatedContracts: undefined,
    agreementsPageTableConfig: {
        rowCount: 15,
        pageNumber: 1,
        idField: 'id',
        columns: [{ name: 'Number', mapping: 'id' }],
    },
    isFormDataComplete: false,
    isOwnerDataComplete: false,
    defaultLessee: undefined,
    dataLoaded: undefined
};

// Action Types
export enum actionTypes {
    GET_LEASE_GENERATION_FORM = '[leaseGeneration] GET_LEASE_GENERATION_FORM',
    GET_LEASE_GENERATION_FORM_SUCCESS = '[leaseGeneration] GET_LEASE_GENERATION_FORM_SUCCESS',
    GET_LEASE_GENERATION_FORM_COLLECTION = '[leaseGeneration] GET_LEASE_GENERATION_FORM_COLLECTION',
    GET_LEASE_GENERATION_FORM_COLLECTION_SUCCESS = '[leaseGeneration] GET_LEASE_GENERATION_FORM_COLLECTION_SUCCESS',
    GET_OWNER_DATA = '[leaseGeneration] GET_OWNER_DATA',
    GET_OWNER_DATA_SUCCESS = '[leaseGeneration] GET_OWNER_DATA_SUCCESS',
    CREATE_LEASE_GENERATION_FORM = '[leaseGeneration] CREATE_LEASE_GENERATION_FORM',
    CREATE_LEASE_GENERATION_FORM_SUCCESS = '[leaseGeneration] CREATE_LEASE_GENERATION_FORM_SUCCESS',
    UPDATE_LEASE_GENERATION_FORM = '[leaseGeneration] UPDATE_LEASE_GENERATION_FORM',
    GET_AGREEMENTS_BY_USER = '[leaseGeneration] GET_AGREEMENTS_BY_USER',
    GET_AGREEMENTS = '[leaseGeneration] GET_AGREEMENTS',
    GET_AGREEMENTS_SUCCESS = '[leaseGeneration] GET_AGREEMENTS_SUCCESS',
    GET_GENERATED_CONTRACTS = '[leaseGeneration] GET_GENERATED_CONTRACTS',
    GET_GENERATED_CONTRACTS_SUCCESS = '[leaseGeneration] GET_GENERATED_CONTRACTS_SUCCESS',
    // NAV_ACQUISITION = '[leaseGeneration] NAV_ACQUISITION',
    INCREMENT_ACQ_PAGE = '[leaseGeneration] INCREMENT_ACQ_PAGE',
    RESET_ACQ_PAGE = '[leaseGeneration] RESET_ACQ_PAGE',
    SET_AGREEMENT_STATUS = '[leaseGeneration] SET_AGREEMENT_STATUS',
    CLEAR_FORM = '[leaseGeneration] CLEAR_FORM',
    GET_DEFAULT_LESSEE = '[leaseGeneration] GET_DEFAULT_LESSEE',
    GET_DEFAULT_LESSEE_SUCCESS = '[leaseGeneration] GET_DEFAULT_LESSEE_SUCCESS',
}

// Action Creator
export const actionCreators = {
    getLeaseGenerationForm: (payload: { projectId: number; ownerId: number; leaseFormId: number, leaseType: number }) => {
        return { type: actionTypes.GET_LEASE_GENERATION_FORM, payload };
    },
    getLeaseGenerationFormSuccess: (leaseData: LeaseGenerationForm) => {
        return { type: actionTypes.GET_LEASE_GENERATION_FORM_SUCCESS, payload: leaseData };
    },
    getLeaseGenerationFormCollection: (payload: { projectId: number; ownerLookUpId: number }) => {
        return { type: actionTypes.GET_LEASE_GENERATION_FORM_COLLECTION, payload };
    },
    getLeaseGenerationFormCollectionSuccess: (leaseCollection: Array<LeaseGenerationForm>) => {
        return { type: actionTypes.GET_LEASE_GENERATION_FORM_COLLECTION_SUCCESS, payload: leaseCollection };
    },
    getOwnerData: (payload: { clientId: number; projectId: number; ownerId: number }) => {
        return { type: actionTypes.GET_OWNER_DATA, payload };
    },
    getOwnerDataSuccess: (leaseGenerationOwnerData: LeaseGenerationOwnerData) => {
        return { type: actionTypes.GET_OWNER_DATA_SUCCESS, payload: leaseGenerationOwnerData };
    },
    createLeaseGenerationForm: (payload: { clientNumber: number; projectId: number; ownerId: number; leaseData: LeaseGenerationForm }) => {
        return { type: actionTypes.CREATE_LEASE_GENERATION_FORM, payload };
    },
    updateLeaseGenerationForm: (payload: { projectNumber: number; ownerId: number; leaseData: LeaseGenerationForm }) => {
        return { type: actionTypes.UPDATE_LEASE_GENERATION_FORM, payload };
    },
    getAgreementsByUser: (payload: { projectNumber: number }) => {
        return { type: actionTypes.GET_AGREEMENTS_BY_USER, payload };
    },
    getGeneratedContracts: (payload: { clientNumber: number; projectNumber: number, options: ODataOptions }) => {
        return { type: actionTypes.GET_GENERATED_CONTRACTS, payload };
    },
    getGeneratedContractsSuccess: (agreements: PagedResultSet<ContractGenerationListDto>) => {
        return { type: actionTypes.GET_GENERATED_CONTRACTS_SUCCESS, payload: { agreements } };
    },
    // navigateAcquisition: (payload: { clientNumber: number; projectNumber: number; ownerLookUpId: number }) => {
    //     return { type: actionTypes.NAV_ACQUISITION, payload };
    // },
    incrementAcqPage: (clientNumber: number, projectNumber: number) => {
        return { type: actionTypes.INCREMENT_ACQ_PAGE, payload: { clientNumber, projectNumber } };
    },
    resetAcqPage: () => {
        return { type: actionTypes.RESET_ACQ_PAGE };
    },
    setAgreementStatus: (payload: {
        clientNumber: number;
        projectNumber: number;
        ownerId: number;
        leaseFormId: number;
        leaseType: number;
        status: number;
        explanation: string;
    }) => {
        return { type: actionTypes.SET_AGREEMENT_STATUS, payload };
    },
    clearForm: () => {
        return { type: actionTypes.CLEAR_FORM };
    },
    getDefaultLessee: (payload: { clientNumber: number; projectNumber: number }) => {
        return { type: actionTypes.GET_DEFAULT_LESSEE, payload };
    },
    getDefaultLesseeSuccess: (payload: IOwner) => {
        return { type: actionTypes.GET_DEFAULT_LESSEE, payload };
    },
};

// Reducer
const reducer: Reducer<ILeaseGenerationState> = (state: ILeaseGenerationState = LeaseGenerationInitialState, action: Action) => {
    switch (action.type) {
        case actionTypes.GET_LEASE_GENERATION_FORM: {
            return {
                ...state,
                formData: undefined,
            };
        }
        case actionTypes.GET_LEASE_GENERATION_FORM_SUCCESS: {
            const payload = (action as { type: string; payload: LeaseGenerationForm }).payload;
            return {
                ...state,
                formData: payload,
                isFormDataComplete: true,
            };
        }
        case actionTypes.GET_OWNER_DATA: {
            return {
                ...state,
                ownerData: undefined,
            };
        }
        case actionTypes.GET_OWNER_DATA_SUCCESS: {
            const payload = (action as { type: string; payload: LeaseGenerationOwnerData }).payload;
            return {
                ...state,
                ownerData: payload,
                isOwnerDataComplete: true,
            };
        }
        case actionTypes.GET_LEASE_GENERATION_FORM_COLLECTION: {
            return {
                ...state,
                leaseCollection: undefined,
            };
        }
        case actionTypes.GET_LEASE_GENERATION_FORM_COLLECTION_SUCCESS: {
            const payload = (action as { type: string; payload: Array<Lease> }).payload;
            return {
                ...state,
                leaseCollection: payload,
            };
        }
        case actionTypes.GET_AGREEMENTS_SUCCESS: {
            const payload = (action as { type: string; payload: { agreements: Array<Lease> } }).payload;
            return {
                ...state,
                agreements: payload.agreements,
            };
            // const getAgreementsPayload = (action as { type: string, payload: { agreements: PagedResultSet<LeaseGenerationForm>, resetPage: boolean } }).payload;

            // if (getAgreementsPayload.resetPage) {
            //     return {
            //         ...state,
            //         agreements: getAgreementsPayload.agreements
            //     };
            // } else if (state.agreements === undefined || state.agreements.pageNumber < getAgreementsPayload.agreements.pageNumber) {
            //     const newAgreements = { ...state.agreements };
            //     newAgreements.pageNumber = getAgreementsPayload.agreements.pageNumber;
            //     newAgreements.pageSize = getAgreementsPayload.agreements.pageSize;
            //     newAgreements.totalRecords = getAgreementsPayload.agreements.totalRecords;
            //     if (!newAgreements.items) {
            //         newAgreements.items = [];
            //     }
            //     newAgreements.items = newAgreements.items.concat(getAgreementsPayload.agreements.items);
            //     return {
            //         ...state,
            //         agreements: newAgreements,
            //     };
            // }

            return state;
        }
        case actionTypes.GET_GENERATED_CONTRACTS: {
            return {
                ...state,
                dataLoaded: false
            };
        }
        case actionTypes.GET_GENERATED_CONTRACTS_SUCCESS: {
            const payload = (action as { type: string; payload: { agreements: PagedResultSet<ContractGenerationListDto> } }).payload;

            const tempContractsResults = _.cloneDeep(state.generatedContracts);

            if (payload.agreements.totalRecords > 0 && !!tempContractsResults) {
                tempContractsResults.items = [...tempContractsResults.items].concat([...payload.agreements.items]);
                return {
                    ...state,
                    generatedContracts: tempContractsResults,
                    dataLoaded: true
                };
            }

            return {
                ...state,
                generatedContracts: payload.agreements,
                dataLoaded: true
            };
        }
        case actionTypes.INCREMENT_ACQ_PAGE: {
            return {
                ...state,
                agreementsPageTableConfig: {
                    ...state.agreementsPageTableConfig,
                    pageNumber: state.agreementsPageTableConfig.pageNumber + 1,
                },
                dataLoaded: false
            };
        }
        case actionTypes.RESET_ACQ_PAGE: {
            return {
                ...state,
                agreementsPageTableConfig: {
                    ...state.agreementsPageTableConfig,
                    pageNumber: 1,
                },
                generatedContracts: undefined
            };
        }
        case actionTypes.CLEAR_FORM: {
            return {
                ...state,
                formData: undefined,
                ownerData: undefined,
                isFormDataComplete: false,
                isOwnerDataComplete: false,
                generatedContracts: undefined,
            };
        }
        case actionTypes.GET_DEFAULT_LESSEE: {
            const payload = (action as { type: string; payload: IOwner }).payload;
            return {
                ...state,
                defaultLessee: payload,
            };
        }
        default:
            return state;
    }
};

export default reducer;

// Selectors
const leaseGenerationFormSelector = (state: IRootState): LeaseGenerationForm => state.leaseGeneration.formData;
const leaseGenerationOwnerDataSelector = (state: IRootState): LeaseGenerationOwnerData => state.leaseGeneration.ownerData;
const leaseGenerationFormCollectionSelector = (state: IRootState): Array<Lease> => state.leaseGeneration.leaseCollection;
const defaultLesseeSelector = (state: IRootState): IOwner => state.leaseGeneration.defaultLessee;

export const selectors = {
    leaseGenerationForm: leaseGenerationFormSelector,
    leaseGenerationOwnerData: leaseGenerationOwnerDataSelector,
    leaseGenerationFormCollection: leaseGenerationFormCollectionSelector,
    agreementsPageTableConfig: (state: IRootState): ITableConfig => state.leaseGeneration.agreementsPageTableConfig,
    // agreements: (state: IRootState): PagedResultSet<LeaseGenerationForm> => state.leaseGeneration.agreements,
    agreements: (state: IRootState): Array<Lease> => state.leaseGeneration.agreements,
    generatedContracts: (state: IRootState): PagedResultSet<ContractGenerationListDto> => state.leaseGeneration.generatedContracts,
    defaultLessee: defaultLesseeSelector,
};

// Side-Effects
const sagas = {
    getLeaseGenerationForm: function* (action: { type: string; payload: { projectId: number; ownerId: number; leaseFormId: number, leaseType: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);

        const [result] = yield all([
            // TODO: add client number

            call(Api.LeaseGeneration.getLeaseGenerationForm, 1, action.payload.projectId, action.payload.ownerId, action.payload.leaseFormId, action.payload.leaseType, jwt),
            delay(200),
        ]);

        yield put(actionCreators.getLeaseGenerationFormSuccess(result));
    },
    getLeaseGenerationFormCollection: function* (action: { type: string; payload: { projectId: number; ownerLookUpId: number, leaseType: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);

        const [result] = yield all([
            // TODO: add client number

            call(Api.LeaseGeneration.getLeaseGenerationFormCollection, 1, action.payload.projectId, action.payload.ownerLookUpId, action.payload.leaseType, jwt),
            delay(200),
        ]);

        yield put(actionCreators.getLeaseGenerationFormCollectionSuccess(result));
    },
    createLeaseGenerationForm: function* (action: {
        type: string;
        payload: { clientNumber: number; projectId: number; ownerId: number; leaseData: LeaseGenerationForm, leaseType: number };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);

        const [result] = yield all([
            // TODO: add client number

            call(
                Api.LeaseGeneration.createLeaseGenerationForm,
                action.payload.clientNumber,
                action.payload.projectId,
                action.payload.ownerId,
                action.payload.leaseData,
                jwt
            ),
            delay(200),
        ]);
        // tslint:disable-next-line
        // const clientId = yield select((state: IRootState) => state.router.location.search.match["clientId"]);
        yield put(
            actionCreators.getLeaseGenerationForm({ projectId: action.payload.projectId, ownerId: action.payload.ownerId, leaseFormId: result, leaseType: action.payload.leaseType})
        );
        yield put(replace('/'));
        yield put(
            replace(
                `/clients/${action.payload.clientNumber}/projects/${action.payload.projectId}/acquisitions/lease-generation/ownerId/${action.payload.ownerId}/leaseFormId/${result}`
            )
        );
    },
    getOwnerData: function* (action: { type: string; payload: { clientId: number; projectId: number; ownerId: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);

        const [result] = yield all([
            // TODO: add client number

            call(Api.LeaseGeneration.getOwnerData, action.payload.clientId, action.payload.projectId, action.payload.ownerId, jwt),
            delay(200),
        ]);

        yield put(actionCreators.getOwnerDataSuccess(result));
    },
    updateLeaseGenerationForm: function* (action: {
        type: string;
        payload: { projectNumber: number; ownerId: number; leaseData: LeaseGenerationForm };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);

        const { projectNumber, ownerId, leaseData } = action.payload;
        // TODO: add client number
        yield call(Api.LeaseGeneration.updateLeaseGenerationForm, 1, projectNumber, ownerId, leaseData, jwt);
    },
    getAgreementsByUser: function* (action: { type: string; payload: { projectNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);

        const { projectNumber } = action.payload;
        // TODO: add client number
        const result = yield call(Api.LeaseGeneration.getAgreementsByUser, 1, projectNumber, jwt);
        yield put(actionCreators.getLeaseGenerationFormCollectionSuccess(result));
    },
    getGeneratedContracts: function* (action: { type: string; payload: { clientNumber: number; projectNumber: number, options: ODataOptions } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const tableConfig = yield select(selectors.agreementsPageTableConfig);
        const options = ODataOptions.from(tableConfig);
        const stringOptions = options.toString();
        if (!(stringOptions.search('orderby') > 0)) {
            options.addOrderBy('leaseNumber', ODataOrderByDirection.Descending);
        }

        if (action.payload) {
            options.merge(action.payload.options);
        }

        const [result] = yield all([
            call(Api.LeaseGeneration.getGeneratedContracts, action.payload.clientNumber, action.payload.projectNumber, options, jwt),
            delay(200),
        ]);

        yield put(actionCreators.getGeneratedContractsSuccess(result));
    },
    // navigateAcquisition: function* (action: {
    //     type: string;
    //     payload: { clientNumber: number; projectNumber: number; ownerLookUpId: number; leaseType: number };
    // }): unknown {
    //     yield call(layoutActionCreators.waitIniitalLoadState);
    //     const jwt = yield select(authSelectors.jwt);

    //     const [result] = yield all([
    //         call(
    //             Api.LeaseGeneration.getLeaseGenerationFormCollection,
    //             action.payload.clientNumber,
    //             action.payload.projectNumber,
    //             action.payload.ownerLookUpId,
    //             action.payload.leaseType,
    //             jwt
    //         ),
    //         delay(200),
    //     ]);
    //     console.log('these are the records for the leasegeneration, ', result);

    //     const recordCount = (result as LeaseGenerationForm[]).length;
    //     // console.log('these are the records for the leasegeneration, ', recordCount);

    //     if (recordCount === 1) {
    //         // tslint:disable-next-line: max-line-length
    //         yield put(
    //             replace(
    //             `/clients/${action.payload.clientNumber}/projects/${action.payload.projectNumber}/acquisitions/lease-generation/ownerId/${action.payload.ownerLookUpId}/leaseFormId/${result[0].id}`
    //             )
    //         );
    //     } else {
    //         yield put(actionCreators.getLeaseGenerationFormCollectionSuccess(result));
    //         yield put(layoutActionCreators.showDialog(DialogList.MANAGE_AGREEMENTS));
    //     }
    // },
    // tslint:disable-next-line: max-line-length
    setAgreementStatus: function* (action: {
        type: string;
        payload: { clientNumber: number; projectNumber: number; ownerId: number; leaseFormId: number; leaseType: number; status: number; explanation: string };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, ownerId, leaseFormId, leaseType, status, explanation } = action.payload;

        yield call(Api.LeaseGeneration.setAgreementStatus, clientNumber, projectNumber, ownerId, leaseFormId, leaseType, status, explanation, jwt);
        yield put(actionCreators.getLeaseGenerationForm({ projectId: projectNumber, ownerId, leaseFormId, leaseType }));
        location.reload();
    },
    getDefaultLessee: function* (action: { type: string; payload: { clientNumber: number; projectNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const projects = yield select(projectSelectors.projects);
        const projectDefaultOwnerLookUpId = (projects as PagedResultSet<Project>)?.items[0]?.defaultOwnerLookUpId;
        if (projectDefaultOwnerLookUpId > 0) {
            const [result] = yield all([
                call(Api.LeaseGeneration.getOwnerData, action.payload.clientNumber, action.payload.projectNumber, projectDefaultOwnerLookUpId, jwt),
                delay(200),
            ]);
            yield put(actionCreators.getDefaultLesseeSuccess(result));
        }
    },
};

// Watches
const watches = {
    getLeaseGenerationForm: function* () {
        yield takeEvery(actionTypes.GET_LEASE_GENERATION_FORM, sagas.getLeaseGenerationForm);
    },
    getLeaseGenerationFormCollection: function* () {
        yield takeEvery(actionTypes.GET_LEASE_GENERATION_FORM_COLLECTION, sagas.getLeaseGenerationFormCollection);
    },
    createLeaseGenerationForm: function* () {
        yield takeEvery(actionTypes.CREATE_LEASE_GENERATION_FORM, sagas.createLeaseGenerationForm);
    },
    getOwnerData: function* () {
        yield takeEvery(actionTypes.GET_OWNER_DATA, sagas.getOwnerData);
    },
    updateLeaseGenerationForm: function* () {
        yield takeEvery(actionTypes.UPDATE_LEASE_GENERATION_FORM, sagas.updateLeaseGenerationForm);
    },
    getAgreementsByUser: function* () {
        yield takeEvery(actionTypes.GET_AGREEMENTS_BY_USER, sagas.getAgreementsByUser);
    },
    getGeneratedContracts: function* () {
        yield takeLatest(actionTypes.GET_GENERATED_CONTRACTS, sagas.getGeneratedContracts);
    },
    // navigateAcquisition: function* () {
    //     yield takeEvery(actionTypes.NAV_ACQUISITION, sagas.navigateAcquisition);
    // },
    incrementAcqPage: function* () {
        yield takeEvery(actionTypes.INCREMENT_ACQ_PAGE, sagas.getGeneratedContracts);
    },
    setAgreementStatus: function* () {
        yield takeEvery(actionTypes.SET_AGREEMENT_STATUS, sagas.setAgreementStatus);
    },
    getDefaultLessee: function* () {
        yield takeEvery(actionTypes.GET_DEFAULT_LESSEE, sagas.getDefaultLessee);
    },
};

// Forks
export function* saga() {
    yield fork(watches.getLeaseGenerationForm);
    yield fork(watches.createLeaseGenerationForm);
    yield fork(watches.getOwnerData);
    yield fork(watches.getLeaseGenerationFormCollection);
    yield fork(watches.updateLeaseGenerationForm);
    yield fork(watches.getAgreementsByUser);
    yield fork(watches.getGeneratedContracts);
    // yield fork(watches.navigateAcquisition);
    yield fork(watches.incrementAcqPage);
    yield fork(watches.setAgreementStatus);
    yield fork(watches.getDefaultLessee);
}



// WEBPACK FOOTER //
// ./src/reducers/LeaseGeneration.ts